import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRightCircle, BookOpen } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { sanityClient } from '../../sanityClient';

interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string;
  mainImage?: { asset: { url: string } };
  publishedAt?: string;
  author?: {
    name: string;
    image?: {
      asset: {
        url: string;
      }
    }
  };
}

async function fetchLatestBlogPosts(): Promise<Post[]> {
  try {
    const posts = await sanityClient.fetch(`
      *[_type == "post"] | order(publishedAt desc)[0...3] {
        _id,
        title,
        slug,
        excerpt,
        mainImage {
          asset-> {
            url
          }
        },
        author-> {
          name,
          image {
            asset-> {
              url
            }
          }
        },
        publishedAt
      }
    `);
    return posts || [];
  } catch (error) {
    console.error("Error fetching latest blog posts:", error);
    return [];
  }
}

export function AboutBlogSection() {
  const { data: posts = [] } = useQuery({
    queryKey: ['latestBlogPosts'],
    queryFn: fetchLatestBlogPosts,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });

  if (!posts.length) return null;

  return (
    <section id="blog" className="py-24 bg-background" aria-label="Latest Blog Posts">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <BookOpen className="w-4 h-4 text-foreground" />
            <span className="text-foreground">Latest Blog Posts</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Latest from Our <span className="text-primary">Blog</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            Stay updated with the latest news, tips, and stories from the Metefy community
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {posts.map(post => (
            <Link 
              to={`/blog/${post.slug.current}`} 
              key={post._id}
              className="block"
            >
              <Card className="h-full bg-card border-2 border-border hover:border-primary hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <CardContent className="p-0">
                  {post.mainImage?.asset?.url && (
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={post.mainImage.asset.url}
                        alt={post.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 text-foreground group-hover:text-primary transition-all duration-200">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="text-foreground/80 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-foreground/80">
                      {post.author && (
                        <div className="flex items-center gap-2">
                          {post.author.image?.asset?.url && (
                            <img
                              src={post.author.image.asset.url}
                              alt={post.author.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          )}
                          <span className="font-semibold text-primary">{post.author.name}</span>
                        </div>
                      )}
                      {post.publishedAt && (
                        <>
                          {post.author && <span>•</span>}
                          <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link to="/blog">
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              View All Blogs
              <ArrowRightCircle className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
} 